<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  useAccountTimeZone,
  todayIn,
} from 'dashboard/composables/useAccountTimeZone';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportMetricCard from 'dashboard/routes/dashboard/settings/reports/components/ReportMetricCard.vue';
import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';
import CrmAPI from 'dashboard/api/crm';
import ContactsAPI from 'dashboard/api/contacts';

const props = defineProps({
  contactId: {
    type: Number,
    required: true,
  },
});
const emit = defineEmits(['back']);

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { timeZone } = useAccountTimeZone();

const loading = ref(true);
const errored = ref(false);
const profile = ref(null);

// -------- rótulos pt-BR (tipo e recorrência da ocasião) --------
const OCCASION_TYPE_LABELS = {
  birthday: 'Aniversário',
  anniversary: 'Data comemorativa',
  seasonal: 'Data sazonal',
  interval: 'Recompra por intervalo',
  custom: 'Personalizada',
};
const RECURRENCE_LABELS = {
  annual_fixed: 'Anual (data fixa)',
  interval: 'Por intervalo de dias',
  seasonal_calendar: 'Calendário sazonal',
};
const occasionTypeLabel = type => OCCASION_TYPE_LABELS[type] || type;
const recurrenceLabel = r => RECURRENCE_LABELS[r] || r;

// -------- formatadores pt-BR (decimais chegam como string) --------
const toNum = v =>
  v === null || v === undefined || v === '' ? null : parseFloat(v);
const fmtBRL = v => {
  const n = toNum(v);
  return n === null
    ? '—'
    : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const fmtInt = v =>
  v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR');
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const fmtDate = value => {
  if (!value) return '—';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { timeZone: timeZone.value });
};
// Data com hora (pedido) vira o dia no fuso da conta, o mesmo que o servidor usa; nunca o do navegador.
const toDateInput = value => {
  if (!value) return '';
  if (DATE_ONLY.test(value)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value).slice(0, 10)
    : todayIn(timeZone.value, d);
};

const contact = computed(() => profile.value?.contact || {});
const aggregates = computed(() => profile.value?.aggregates || {});
const orders = computed(() => profile.value?.orders || []);
const occasions = computed(() => profile.value?.occasions || []);
const company = computed(() => profile.value?.company || null);
const deals = computed(() => profile.value?.deals || []);
const dealsSummary = computed(() => profile.value?.deals_summary || {});

// -------- negócios (deals do Kanban) --------
const DEAL_STATUS = {
  won: {
    label: 'Ganho',
    class: 'bg-n-teal-3 text-n-teal-11',
  },
  lost: {
    label: 'Perdido',
    class: 'bg-n-ruby-3 text-n-ruby-11',
  },
  open: {
    label: 'Em aberto',
    class: 'bg-n-blue-3 text-n-blue-9',
  },
};
const dealStatus = status => DEAL_STATUS[status] || DEAL_STATUS.open;

const dealRoute = deal =>
  accountScopedRoute('kanban_task_show', {
    boardId: deal.board_id,
    taskId: deal.id,
  });

const initials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const orderItemsSummary = order => {
  const items = order.order_items || [];
  if (!items.length) return '—';
  return items.map(i => i.product_name).join(', ');
};

const fetchProfile = async () => {
  loading.value = true;
  errored.value = false;
  try {
    const { data } = await CrmAPI.customerProfile(props.contactId);
    profile.value = data;
  } catch (e) {
    errored.value = true;
  } finally {
    loading.value = false;
  }
};

// ---------------- Autorizacao para oferta (LGPD) ----------------
// O "pode receber" do servidor ja considera a opcao da conta ("quem comprou pode
// receber") e um SAIR que o cliente tenha mandado.
const savingConsent = ref(false);
const optInFlag = computed(
  () => profile.value?.contact?.additional_attributes?.crm_opt_in
);
const toggleConsent = async () => {
  savingConsent.value = true;
  try {
    const allowed = !profile.value.can_receive_offers;
    const { data } = await CrmAPI.setConsent(props.contactId, allowed);
    profile.value.can_receive_offers = data.can_receive_offers;
    profile.value.contact.additional_attributes = {
      ...(profile.value.contact.additional_attributes || {}),
      crm_opt_in: allowed,
    };
  } catch (e) {
    useAlert(t('CRM_RECOMPRA.CONSENT.ERROR'));
  } finally {
    savingConsent.value = false;
  }
};

// ---------------- Ocasiões (modal) ----------------
const showOccasionModal = ref(false);
const editingOccasionId = ref(null);
const savingOccasion = ref(false);

const emptyOccasion = () => ({
  occasion_type: 'birthday',
  recurrence_mode: 'annual_fixed',
  base_date: '',
  interval_days: '',
  lead_days: '',
  recipient_name: '',
  relationship: '',
  title: '',
  active: true,
});
const occasionForm = ref(emptyOccasion());

const openNewOccasion = () => {
  occasionForm.value = emptyOccasion();
  editingOccasionId.value = null;
  showOccasionModal.value = true;
};

const openEditOccasion = occasion => {
  occasionForm.value = {
    occasion_type: occasion.occasion_type,
    recurrence_mode: occasion.recurrence_mode,
    base_date: toDateInput(occasion.base_date),
    interval_days: occasion.interval_days ?? '',
    lead_days: occasion.lead_days ?? '',
    recipient_name: occasion.recipient_name || '',
    relationship: occasion.relationship || '',
    title: occasion.title || '',
    active: occasion.active,
  };
  editingOccasionId.value = occasion.id;
  showOccasionModal.value = true;
};

const buildOccasionPayload = () => {
  const f = occasionForm.value;
  return {
    occasion: {
      contact_id: props.contactId,
      occasion_type: f.occasion_type,
      recurrence_mode: f.recurrence_mode,
      base_date: f.base_date || null,
      interval_days: f.interval_days === '' ? null : Number(f.interval_days),
      lead_days: f.lead_days === '' ? null : Number(f.lead_days),
      recipient_name: f.recipient_name || null,
      relationship: f.relationship || null,
      title: f.title || null,
      active: f.active,
    },
  };
};

const saveOccasion = async () => {
  savingOccasion.value = true;
  try {
    const payload = buildOccasionPayload();
    if (editingOccasionId.value) {
      await CrmAPI.updateOccasion(editingOccasionId.value, payload);
      useAlert('Ocasião atualizada');
    } else {
      await CrmAPI.createOccasion(payload);
      useAlert('Ocasião adicionada');
    }
    showOccasionModal.value = false;
    await fetchProfile();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao salvar a ocasião');
  } finally {
    savingOccasion.value = false;
  }
};

const removeOccasion = async occasion => {
  if (!window.confirm('Remover esta ocasião?')) return;
  try {
    await CrmAPI.deleteOccasion(occasion.id);
    await fetchProfile();
  } catch (e) {
    useAlert('Erro ao remover a ocasião');
  }
};

// ---------------- Pedidos (modal) ----------------
const showOrderModal = ref(false);
const editingOrderId = ref(null);
const savingOrder = ref(false);
// itens marcados para remoção (edição) mantendo id + _destroy
const removedOrderItems = ref([]);

const emptyOrder = () => ({
  ordered_at: '',
  total: '',
  recipient_name: '',
  occasion_id: '',
  channel: '',
  notes: '',
  items: [],
});
const orderForm = ref(emptyOrder());

const addOrderItem = () => {
  orderForm.value.items.push({
    product_name: '',
    quantity: '',
    unit_price: '',
  });
};
const removeOrderItem = idx => {
  const item = orderForm.value.items[idx];
  if (item?.id) removedOrderItems.value.push({ id: item.id, _destroy: true });
  orderForm.value.items.splice(idx, 1);
};

const openNewOrder = () => {
  orderForm.value = emptyOrder();
  orderForm.value.items = [{ product_name: '', quantity: '', unit_price: '' }];
  editingOrderId.value = null;
  removedOrderItems.value = [];
  showOrderModal.value = true;
};

const openEditOrder = order => {
  orderForm.value = {
    ordered_at: toDateInput(order.ordered_at),
    total: order.total ?? '',
    recipient_name: order.recipient_name || '',
    occasion_id: order.occasion_id || '',
    channel: order.channel || '',
    notes: order.notes || '',
    items: (order.order_items || []).map(i => ({
      id: i.id,
      product_name: i.product_name || '',
      quantity: i.quantity ?? '',
      unit_price: i.unit_price ?? '',
    })),
  };
  editingOrderId.value = order.id;
  removedOrderItems.value = [];
  showOrderModal.value = true;
};

const buildOrderPayload = () => {
  const f = orderForm.value;
  // monta com push (nunca por índice) para evitar array esparso
  const order_items_attributes = [];
  f.items.forEach(item => {
    if (!item.product_name && !item.id) return;
    const attrs = {
      product_name: item.product_name,
      quantity: item.quantity === '' ? null : Number(item.quantity),
      unit_price: item.unit_price === '' ? null : Number(item.unit_price),
    };
    if (item.id) attrs.id = item.id;
    order_items_attributes.push(attrs);
  });
  removedOrderItems.value.forEach(r => order_items_attributes.push(r));
  return {
    order: {
      contact_id: props.contactId,
      ordered_at: f.ordered_at || null,
      total: f.total === '' ? null : Number(f.total),
      recipient_name: f.recipient_name || null,
      occasion_id: f.occasion_id || null,
      channel: f.channel || null,
      notes: f.notes || null,
      order_items_attributes,
    },
  };
};

const saveOrder = async () => {
  savingOrder.value = true;
  try {
    const payload = buildOrderPayload();
    if (editingOrderId.value) {
      await CrmAPI.updateOrder(editingOrderId.value, payload);
      useAlert('Pedido atualizado');
    } else {
      await CrmAPI.createOrder(payload);
      useAlert('Pedido adicionado');
    }
    showOrderModal.value = false;
    await fetchProfile();
  } catch (e) {
    useAlert(
      e?.response?.data?.message ||
        e?.response?.data?.error ||
        'Erro ao salvar o pedido'
    );
  } finally {
    savingOrder.value = false;
  }
};

const removeOrder = async order => {
  if (!window.confirm('Remover este pedido?')) return;
  try {
    await CrmAPI.deleteOrder(order.id);
    await fetchProfile();
  } catch (e) {
    useAlert('Erro ao remover o pedido');
  }
};

// ---------------- Atributos personalizados do contato ----------------
const ATTRIBUTE_COMPONENTS = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
};
const attributeComponent = type => ATTRIBUTE_COMPONENTS[type] || OtherAttribute;

const contactAttributeDefs = useMapGetter('attributes/getContactAttributes');

// definições de atributo do contato + valor atual (vazio = ainda não preenchido)
const customAttributeItems = computed(() => {
  const values = contact.value.custom_attributes || {};
  return (contactAttributeDefs.value || []).map(def => ({
    ...def,
    value: values[def.attributeKey] ?? '',
  }));
});

const handleAttributeUpdate = async (attribute, value) => {
  try {
    await store.dispatch('contacts/update', {
      id: props.contactId,
      customAttributes: { [attribute.attributeKey]: value },
    });
    useAlert('Atributo atualizado');
    await fetchProfile();
  } catch (e) {
    useAlert('Não foi possível salvar o atributo');
  }
};

const handleAttributeDelete = async attribute => {
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: props.contactId,
      customAttributes: [attribute.attributeKey],
    });
    await fetchProfile();
  } catch (e) {
    useAlert('Não foi possível remover o atributo');
  }
};

// ---------------- Conversas do cliente ----------------
const getInbox = useMapGetter('inboxes/getInbox');

const conversations = ref([]);
const loadingConversations = ref(true);
const conversationsErrored = ref(false);

const CONVERSATION_STATUS_LABELS = {
  open: 'Aberta',
  resolved: 'Resolvida',
  pending: 'Pendente',
  snoozed: 'Adiada',
};
const conversationStatusLabel = status =>
  CONVERSATION_STATUS_LABELS[status] || status;

const fmtDateTime = ts => {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const inboxName = conversation => getInbox.value(conversation.inbox_id)?.name;

const conversationRoute = conversation =>
  accountScopedRoute('conversation_through_inbox', {
    inbox_id: conversation.inbox_id,
    conversation_id: conversation.id,
  });

const fetchConversations = async () => {
  loadingConversations.value = true;
  conversationsErrored.value = false;
  try {
    const { data } = await ContactsAPI.getConversations(props.contactId);
    conversations.value = data.payload || [];
  } catch (e) {
    conversationsErrored.value = true;
  } finally {
    loadingConversations.value = false;
  }
};

onMounted(() => {
  fetchProfile();
  fetchConversations();
  if (!contactAttributeDefs.value?.length) store.dispatch('attributes/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col w-full">
    <header class="sticky top-0 z-10 px-6 bg-n-background">
      <div class="flex items-center justify-between w-full h-20 gap-2">
        <Button
          label="Voltar para clientes"
          icon="i-lucide-arrow-left"
          variant="faded"
          color="slate"
          size="sm"
          @click="emit('back')"
        />
      </div>
    </header>

    <div class="flex-1 px-6 pb-8">
      <div class="max-w-5xl mx-auto w-full flex flex-col gap-5 pt-1">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex items-center justify-center gap-2 p-16 text-sm text-n-slate-11"
        >
          <span class="i-lucide-loader-circle size-4 animate-spin" />
          Carregando perfil do cliente...
        </div>

        <!-- Erro -->
        <div
          v-else-if="errored"
          class="flex flex-col items-center justify-center gap-3 py-24 text-center"
        >
          <span class="i-lucide-triangle-alert size-10 text-n-slate-9" />
          <p class="text-[15px] font-semibold text-n-slate-12">
            Não foi possível carregar o perfil
          </p>
          <Button
            label="Tentar novamente"
            icon="i-lucide-refresh-cw"
            variant="faded"
            color="slate"
            size="sm"
            @click="fetchProfile"
          />
        </div>

        <template v-else>
          <!-- Cabeçalho do cliente -->
          <div
            class="flex items-center gap-4 rounded-2xl border border-n-container bg-n-solid-1 p-6"
          >
            <img
              v-if="contact.thumbnail"
              :src="contact.thumbnail"
              class="size-14 rounded-full object-cover shrink-0"
            />
            <span
              v-else
              class="flex items-center justify-center size-14 rounded-full bg-n-blue-3 text-base font-semibold text-n-blue-9 shrink-0"
            >
              {{ initials(contact.name) }}
            </span>
            <div class="flex flex-col gap-0.5 min-w-0">
              <span
                class="text-lg font-semibold tracking-[-0.02em] text-n-slate-12 truncate"
              >
                {{ contact.name || 'Sem nome' }}
              </span>
              <div
                class="flex items-center gap-3 text-[13px] text-n-slate-11 flex-wrap"
              >
                <span
                  v-if="contact.phone_number"
                  class="inline-flex items-center gap-1.5"
                >
                  <span class="i-lucide-phone size-3.5" />
                  {{ contact.phone_number }}
                </span>
                <span
                  v-if="contact.email"
                  class="inline-flex items-center gap-1.5"
                >
                  <span class="i-lucide-mail size-3.5" /> {{ contact.email }}
                </span>
                <span v-if="company" class="inline-flex items-center gap-1.5">
                  <span class="i-lucide-building-2 size-3.5" />
                  Empresa: {{ company.name }}
                </span>
              </div>
              <label
                class="inline-flex items-center gap-2 mt-1 text-[13px] cursor-pointer"
                :class="
                  profile.can_receive_offers
                    ? 'text-n-teal-11'
                    : 'text-n-slate-11'
                "
              >
                <input
                  type="checkbox"
                  class="accent-n-brand"
                  :checked="profile.can_receive_offers"
                  :disabled="savingConsent"
                  @change="toggleConsent"
                />
                {{
                  profile.can_receive_offers
                    ? t('CRM_RECOMPRA.CONSENT.YES')
                    : optInFlag === false
                      ? t('CRM_RECOMPRA.CONSENT.OPTED_OUT')
                      : t('CRM_RECOMPRA.CONSENT.NO')
                }}
              </label>
            </div>
          </div>

          <!-- Cards de métrica -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ReportMetricCard
              label="Total gasto"
              :value="fmtBRL(aggregates.total_spent)"
              info-text="Soma de todos os pedidos do cliente"
              class="rounded-xl border border-n-container bg-n-solid-1 p-4"
            />
            <ReportMetricCard
              label="Nº de pedidos"
              :value="fmtInt(aggregates.orders_count)"
              info-text="Quantidade de compras registradas"
              class="rounded-xl border border-n-container bg-n-solid-1 p-4"
            />
            <ReportMetricCard
              label="Ticket médio"
              :value="fmtBRL(aggregates.average_ticket)"
              info-text="Valor médio por pedido"
              class="rounded-xl border border-n-container bg-n-solid-1 p-4"
            />
            <ReportMetricCard
              label="Última compra"
              :value="fmtDate(aggregates.last_order_at)"
              :info-text="`Primeira compra em ${fmtDate(aggregates.first_order_at)}`"
              class="rounded-xl border border-n-container bg-n-solid-1 p-4"
            />
          </div>

          <!-- Negócios (Kanban) -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-6 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-briefcase size-4 text-n-slate-11" />
              <h3 class="text-[15px] font-semibold text-n-slate-12">
                Negócios
              </h3>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <ReportMetricCard
                label="Em aberto"
                :value="fmtBRL(dealsSummary.open_value)"
                :info-text="`${fmtInt(dealsSummary.open_count)} negócio(s) em aberto`"
                class="rounded-xl bg-n-slate-2 p-4"
              />
              <ReportMetricCard
                label="Ganhos"
                :value="fmtBRL(dealsSummary.won_value)"
                :info-text="`${fmtInt(dealsSummary.won_count)} negócio(s) ganho(s)`"
                class="rounded-xl bg-n-slate-2 p-4"
              />
            </div>

            <div
              v-if="!deals.length"
              class="rounded-lg bg-n-slate-2 px-4 py-6 text-center text-[13px] text-n-slate-11"
            >
              Nenhum negócio ainda.
            </div>

            <div v-else class="flex flex-col gap-2">
              <router-link
                v-for="deal in deals"
                :key="deal.id"
                :to="dealRoute(deal)"
                class="flex flex-row items-center justify-between gap-3 rounded-lg border border-n-weak px-4 py-3 hover:bg-n-slate-2 transition-colors"
              >
                <div class="flex flex-col gap-1 min-w-0">
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ deal.title }}
                    </span>
                    <span
                      class="text-xs font-medium inline-flex items-center h-5 px-1.5 rounded-md shrink-0"
                      :class="dealStatus(deal.status).class"
                    >
                      {{ dealStatus(deal.status).label }}
                    </span>
                  </div>
                  <div
                    class="flex items-center gap-3 text-xs text-n-slate-11 flex-wrap"
                  >
                    <span v-if="deal.board_name">{{ deal.board_name }}</span>
                    <span v-if="deal.board_name && deal.stage_name">·</span>
                    <span v-if="deal.stage_name">{{ deal.stage_name }}</span>
                    <span>· {{ fmtBRL(deal.value) }}</span>
                    <span v-if="deal.expected_close_date"
                      >· fecha em {{ fmtDate(deal.expected_close_date) }}</span
                    >
                  </div>
                </div>
                <span
                  class="i-lucide-chevron-right size-4 text-n-slate-10 shrink-0"
                />
              </router-link>
            </div>
          </div>

          <!-- Atributos personalizados -->
          <div
            v-if="customAttributeItems.length"
            class="rounded-2xl border border-n-container bg-n-solid-1 p-6 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-tags size-4 text-n-slate-11" />
              <h3 class="text-[15px] font-semibold text-n-slate-12">
                Atributos personalizados
              </h3>
            </div>

            <div class="flex flex-col gap-2">
              <div
                v-for="attribute in customAttributeItems"
                :key="attribute.id"
                class="grid grid-cols-[160px,1fr] group/attribute items-center gap-2 min-h-10"
              >
                <span
                  class="text-sm font-medium truncate text-n-slate-12"
                  :title="attribute.attributeDisplayName"
                >
                  {{ attribute.attributeDisplayName }}
                </span>
                <component
                  :is="attributeComponent(attribute.attributeDisplayType)"
                  is-editing-view
                  :attribute="attribute"
                  @update="handleAttributeUpdate(attribute, $event)"
                  @delete="handleAttributeDelete(attribute)"
                />
              </div>
            </div>
          </div>

          <!-- Ocasiões / Datas -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-6 flex flex-col gap-4"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <span class="i-lucide-calendar-heart size-4 text-n-slate-11" />
                <h3 class="text-[15px] font-semibold text-n-slate-12">
                  Ocasiões e datas
                </h3>
              </div>
              <Button
                label="Adicionar ocasião"
                icon="i-lucide-plus"
                variant="faded"
                color="slate"
                size="sm"
                @click="openNewOccasion"
              />
            </div>

            <div
              v-if="!occasions.length"
              class="rounded-lg bg-n-slate-2 px-4 py-6 text-center text-[13px] text-n-slate-11"
            >
              Nenhuma data cadastrada. Adicione aniversários e datas de recompra
              para lembrar o cliente na hora certa.
            </div>

            <div
              v-for="occasion in occasions"
              :key="occasion.id"
              class="flex flex-row items-center justify-between gap-3 rounded-lg border border-n-weak px-4 py-3"
            >
              <div class="flex flex-col gap-1 min-w-0">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{
                      occasion.title ||
                      occasionTypeLabel(occasion.occasion_type)
                    }}
                  </span>
                  <span
                    v-if="!occasion.active"
                    class="text-xs font-medium inline-flex items-center h-5 px-1.5 rounded-md bg-n-slate-3 text-n-slate-11 shrink-0"
                  >
                    Inativa
                  </span>
                </div>
                <div
                  class="flex items-center gap-3 text-xs text-n-slate-11 flex-wrap"
                >
                  <span>{{ occasionTypeLabel(occasion.occasion_type) }}</span>
                  <span>·</span>
                  <span>{{ recurrenceLabel(occasion.recurrence_mode) }}</span>
                  <span v-if="occasion.base_date"
                    >· {{ fmtDate(occasion.base_date) }}</span
                  >
                  <span v-if="occasion.recipient_name"
                    >· para {{ occasion.recipient_name }}</span
                  >
                </div>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <Button
                  icon="i-lucide-pencil"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  title="Editar"
                  @click="openEditOccasion(occasion)"
                />
                <Button
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  title="Remover"
                  @click="removeOccasion(occasion)"
                />
              </div>
            </div>
          </div>

          <!-- Histórico de compras -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-6 flex flex-col gap-4"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <span class="i-lucide-shopping-bag size-4 text-n-slate-11" />
                <h3 class="text-[15px] font-semibold text-n-slate-12">
                  Histórico de compras
                </h3>
              </div>
              <Button
                label="Adicionar pedido"
                icon="i-lucide-plus"
                variant="faded"
                color="slate"
                size="sm"
                @click="openNewOrder"
              />
            </div>

            <div
              v-if="!orders.length"
              class="rounded-lg bg-n-slate-2 px-4 py-6 text-center text-[13px] text-n-slate-11"
            >
              Nenhum pedido registrado para este cliente ainda.
            </div>

            <div v-else class="-mx-6 -mb-6 overflow-x-auto">
              <table class="w-full text-[13px]">
                <thead>
                  <tr
                    class="text-left text-[11px] uppercase tracking-[0.04em] text-n-slate-9 bg-n-slate-2 border-b border-n-slate-3"
                  >
                    <th class="py-2.5 pl-6 pr-3 font-semibold">Data</th>
                    <th class="py-2.5 pr-3 font-semibold">Itens</th>
                    <th class="py-2.5 pr-3 font-semibold text-right">Total</th>
                    <th class="py-2.5 pr-6 font-semibold text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="order in orders"
                    :key="order.id"
                    class="border-b border-n-slate-3 last:border-b-0 hover:bg-n-slate-2 transition-colors"
                  >
                    <td
                      class="py-3 pl-6 pr-3 text-n-slate-12 whitespace-nowrap"
                    >
                      {{ fmtDate(order.ordered_at) }}
                    </td>
                    <td
                      class="py-3 pr-3 text-n-slate-11 max-w-[320px] truncate"
                      :title="orderItemsSummary(order)"
                    >
                      {{ orderItemsSummary(order) }}
                    </td>
                    <td
                      class="py-3 pr-3 text-right font-medium text-n-slate-12 whitespace-nowrap"
                    >
                      {{ fmtBRL(order.total) }}
                    </td>
                    <td class="py-3 pr-6 text-right whitespace-nowrap">
                      <Button
                        icon="i-lucide-pencil"
                        variant="ghost"
                        color="slate"
                        size="sm"
                        title="Editar"
                        @click="openEditOrder(order)"
                      />
                      <Button
                        icon="i-lucide-trash-2"
                        variant="ghost"
                        color="ruby"
                        size="sm"
                        title="Remover"
                        @click="removeOrder(order)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Conversas do cliente -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-6 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-messages-square size-4 text-n-slate-11" />
              <h3 class="text-[15px] font-semibold text-n-slate-12">
                Conversas
              </h3>
            </div>

            <div
              v-if="loadingConversations"
              class="flex items-center justify-center gap-2 py-6 text-sm text-n-slate-11"
            >
              <span class="i-lucide-loader-circle size-4 animate-spin" />
              Carregando conversas...
            </div>

            <div
              v-else-if="conversationsErrored"
              class="rounded-lg bg-n-slate-2 px-4 py-6 text-center text-[13px] text-n-slate-11"
            >
              Não foi possível carregar as conversas.
            </div>

            <div
              v-else-if="!conversations.length"
              class="rounded-lg bg-n-slate-2 px-4 py-6 text-center text-[13px] text-n-slate-11"
            >
              Nenhuma conversa ainda.
            </div>

            <div v-else class="flex flex-col gap-2">
              <router-link
                v-for="conversation in conversations"
                :key="conversation.id"
                :to="conversationRoute(conversation)"
                class="flex flex-row items-center justify-between gap-3 rounded-lg border border-n-weak px-4 py-3 hover:bg-n-slate-2 transition-colors"
              >
                <div class="flex flex-col gap-1 min-w-0">
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      Conversa #{{ conversation.id }}
                    </span>
                    <span
                      class="text-xs font-medium inline-flex items-center h-5 px-1.5 rounded-md shrink-0"
                      :class="
                        conversation.status === 'resolved'
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-slate-3 text-n-slate-11'
                      "
                    >
                      {{ conversationStatusLabel(conversation.status) }}
                    </span>
                  </div>
                  <div
                    class="flex items-center gap-3 text-xs text-n-slate-11 flex-wrap"
                  >
                    <span v-if="inboxName(conversation)">
                      {{ inboxName(conversation) }}
                    </span>
                    <span v-if="inboxName(conversation)">·</span>
                    <span>{{
                      fmtDateTime(conversation.last_activity_at)
                    }}</span>
                  </div>
                </div>
                <span
                  class="i-lucide-chevron-right size-4 text-n-slate-10 shrink-0"
                />
              </router-link>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal de ocasião -->
    <div
      v-if="showOccasionModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="showOccasionModal = false"
    >
      <div
        class="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-n-solid-1 shadow-xl border border-n-container overflow-hidden"
      >
        <div class="flex items-center justify-between gap-3 px-6 pt-5 pb-4">
          <h2 class="text-[15px] font-semibold text-n-slate-12">
            {{ editingOccasionId ? 'Editar ocasião' : 'Nova ocasião' }}
          </h2>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="showOccasionModal = false"
          />
        </div>
        <div
          class="flex-1 overflow-auto border-t border-n-slate-3 px-6 py-5 flex flex-col gap-4"
        >
          <label class="flex flex-col gap-1.5">
            <span class="text-sm text-n-slate-11">Título (opcional)</span>
            <input
              v-model="occasionForm.title"
              placeholder="ex: Aniversário da Ana"
              class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
            />
          </label>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Tipo</span>
              <select
                v-model="occasionForm.occasion_type"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              >
                <option value="birthday">Aniversário</option>
                <option value="anniversary">Data comemorativa</option>
                <option value="seasonal">Data sazonal</option>
                <option value="interval">Recompra por intervalo</option>
                <option value="custom">Personalizada</option>
              </select>
            </label>
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Recorrência</span>
              <select
                v-model="occasionForm.recurrence_mode"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              >
                <option value="annual_fixed">Anual (data fixa)</option>
                <option value="interval">Por intervalo de dias</option>
                <option value="seasonal_calendar">Calendário sazonal</option>
              </select>
            </label>
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Data base</span>
              <input
                v-model="occasionForm.base_date"
                type="date"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11"
                >Antecedência do lembrete (dias)</span
              >
              <input
                v-model="occasionForm.lead_days"
                type="number"
                min="0"
                placeholder="ex: 3"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
          </div>
          <label
            v-if="occasionForm.recurrence_mode === 'interval'"
            class="flex flex-col gap-1.5"
          >
            <span class="text-sm text-n-slate-11">Intervalo (dias)</span>
            <input
              v-model="occasionForm.interval_days"
              type="number"
              min="1"
              placeholder="ex: 30"
              class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
            />
          </label>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11"
                >Nome do homenageado (opcional)</span
              >
              <input
                v-model="occasionForm.recipient_name"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Relação (opcional)</span>
              <input
                v-model="occasionForm.relationship"
                placeholder="ex: filha, esposa"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
          </div>
          <label class="inline-flex items-center gap-2 text-sm text-n-slate-11">
            <input
              v-model="occasionForm.active"
              type="checkbox"
              class="accent-n-brand"
            />
            Ocasião ativa (gera lembretes)
          </label>
        </div>
        <div
          class="flex items-center justify-end gap-3 border-t border-n-slate-3 px-6 py-4"
        >
          <Button
            label="Cancelar"
            variant="ghost"
            color="slate"
            @click="showOccasionModal = false"
          />
          <Button
            label="Salvar"
            :is-loading="savingOccasion"
            @click="saveOccasion"
          />
        </div>
      </div>
    </div>

    <!-- Modal de pedido -->
    <div
      v-if="showOrderModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="showOrderModal = false"
    >
      <div
        class="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-n-solid-1 shadow-xl border border-n-container overflow-hidden"
      >
        <div class="flex items-center justify-between gap-3 px-6 pt-5 pb-4">
          <h2 class="text-[15px] font-semibold text-n-slate-12">
            {{ editingOrderId ? 'Editar pedido' : 'Novo pedido' }}
          </h2>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="showOrderModal = false"
          />
        </div>
        <div
          class="flex-1 overflow-auto border-t border-n-slate-3 px-6 py-5 flex flex-col gap-4"
        >
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Data do pedido</span>
              <input
                v-model="orderForm.ordered_at"
                type="date"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Total (R$)</span>
              <input
                v-model="orderForm.total"
                type="number"
                min="0"
                step="0.01"
                placeholder="Deixe vazio para somar os itens"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Canal (opcional)</span>
              <input
                v-model="orderForm.channel"
                placeholder="ex: WhatsApp, Loja"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
            <label class="flex flex-col gap-1.5">
              <span class="text-sm text-n-slate-11">Ocasião (opcional)</span>
              <select
                v-model="orderForm.occasion_id"
                class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              >
                <option value="">Nenhuma</option>
                <option
                  v-for="occasion in occasions"
                  :key="occasion.id"
                  :value="occasion.id"
                >
                  {{
                    occasion.title || occasionTypeLabel(occasion.occasion_type)
                  }}
                </option>
              </select>
            </label>
          </div>
          <label class="flex flex-col gap-1.5">
            <span class="text-sm text-n-slate-11"
              >Nome do presenteado (opcional)</span
            >
            <input
              v-model="orderForm.recipient_name"
              class="h-10 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
            />
          </label>

          <!-- Itens do pedido -->
          <div
            class="flex flex-col gap-2 rounded-lg border border-n-container bg-n-slate-2 p-4"
          >
            <div class="flex items-center justify-between">
              <span class="text-[13px] font-semibold text-n-slate-12">
                Itens
              </span>
              <Button
                label="Adicionar item"
                icon="i-lucide-plus"
                variant="link"
                size="sm"
                @click="addOrderItem"
              />
            </div>
            <p v-if="!orderForm.items.length" class="text-xs text-n-slate-11">
              Opcional: liste os produtos deste pedido.
            </p>
            <div
              v-for="(item, idx) in orderForm.items"
              :key="idx"
              class="flex items-end gap-2"
            >
              <label class="flex flex-col gap-1 flex-1 min-w-0">
                <span class="text-xs text-n-slate-11">Produto</span>
                <input
                  v-model="item.product_name"
                  placeholder="Nome do produto"
                  class="h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
                />
              </label>
              <label class="flex flex-col gap-1 w-20 shrink-0">
                <span class="text-xs text-n-slate-11">Qtd.</span>
                <input
                  v-model="item.quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  class="h-9 px-2 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none focus:border-n-brand transition-colors"
                />
              </label>
              <label class="flex flex-col gap-1 w-28 shrink-0">
                <span class="text-xs text-n-slate-11">Preço un.</span>
                <input
                  v-model="item.unit_price"
                  type="number"
                  min="0"
                  step="0.01"
                  class="h-9 px-2 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none focus:border-n-brand transition-colors"
                />
              </label>
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                color="ruby"
                size="sm"
                @click="removeOrderItem(idx)"
              />
            </div>
          </div>

          <label class="flex flex-col gap-1.5">
            <span class="text-sm text-n-slate-11">Observações (opcional)</span>
            <textarea
              v-model="orderForm.notes"
              rows="2"
              class="p-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
            />
          </label>
        </div>
        <div
          class="flex items-center justify-end gap-3 border-t border-n-slate-3 px-6 py-4"
        >
          <Button
            label="Cancelar"
            variant="ghost"
            color="slate"
            @click="showOrderModal = false"
          />
          <Button label="Salvar" :is-loading="savingOrder" @click="saveOrder" />
        </div>
      </div>
    </div>
  </div>
</template>
